import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { RenderSettings } from '../lib/settings';

/**
 * The staged batch — UI state only (§49). "Add to batch" in the editor puts an item here; "Run batch" on the queue
 * page posts them all as one server batch. Nothing here is the record: once posted, the jobs are. It is mirrored to
 * sessionStorage so a reload between "Add to batch" and "Run batch" does not lose the staging — the tab, not the
 * account, owns it.
 */
export interface StagedItem {
  key: string;
  assetId: string;
  assetName: string;
  assetWidth: number;
  assetHeight: number;
  bytesIn: number;
  presetId: string | null;
  presetName: string;
  formats: string[];
  settings: RenderSettings;
}

interface BatchState {
  items: StagedItem[];
  /** Jobs finished before this instant are hidden from the queue page ("Clear results"). */
  hideFinishedBefore: string | null;
  /** Batch ids posted from this browser, newest first — the queue page shows their jobs even once they settle. */
  batchIds: string[];
  add: (item: Omit<StagedItem, 'key'>) => void;
  remove: (key: string) => void;
  clearStaged: () => void;
  clearResults: () => void;
  rememberBatch: (id: string) => void;
}

export const useBatch = create<BatchState>()(persist((set) => ({
  items: [],
  hideFinishedBefore: null,
  batchIds: [],
  add: (item) => set((s) => ({ items: [...s.items, { ...item, key: `${item.assetId}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}` }] })),
  remove: (key) => set((s) => ({ items: s.items.filter((i) => i.key !== key) })),
  clearStaged: () => set({ items: [] }),
  clearResults: () => set({ hideFinishedBefore: new Date().toISOString() }),
  rememberBatch: (id) => set((s) => ({ batchIds: [id, ...s.batchIds].slice(0, 20) })),
}), { name: 'photo.batch', storage: createJSONStorage(() => sessionStorage) }));
